/**
 * The traveller's view of their Pulo Assistant bookings on My Trips.
 *
 * Tabs follow the booking's own status, never its calendar dates: a trip is
 * upcoming until both sides confirm the start, in progress until both confirm
 * the end, and past once it is completed or dead. Filing by date instead would
 * put a trip that started early — or one still waiting on the traveller's
 * confirmation — under the wrong tab, away from the button they need.
 */
import { formatCurrency } from "./currency";
import { buildConfirmationFlags } from "./tripConfirmation";

export const TRIP_TABS = [
  { key: "upcoming", label: "Upcoming" },
  { key: "in_progress", label: "In progress" },
  { key: "past", label: "Past" },
];

export const BOOKING_STATUS_LABELS = {
  failed_payment: "Payment not completed",
  pending_assistant_action: "Awaiting assistant",
  accepted: "Accepted",
  rejected: "Rejected",
  assistant_arrived: "Assistant arrived",
  trip_started: "Trip in progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

const TAB_BY_STATUS = {
  pending_assistant_action: "upcoming",
  accepted: "upcoming",
  assistant_arrived: "upcoming",
  trip_started: "in_progress",
  completed: "past",
  cancelled: "past",
  rejected: "past",
  failed_payment: "past",
};

const CANCELLABLE_STATUSES = new Set(["pending_assistant_action", "accepted"]);

const normalizeStatus = (status) => String(status || "").trim().toLowerCase();

export const getTripTab = (status) => TAB_BY_STATUS[normalizeStatus(status)] || "past";

const toDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDay = (date) =>
  date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const formatDayTime = (date) =>
  date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

// Mirrors config.booking.cancellationCutoffHours on the server: cancelling
// closes this long before the trip starts, and is refused after that.
const CANCELLATION_CUTOFF_HOURS = 12;
const CANCELLATION_CUTOFF_MS = CANCELLATION_CUTOFF_HOURS * 60 * 60 * 1000;

// Derived from the trip's own start rather than read off the booking, so it is
// there before the assistant has accepted — which is when a traveller is most
// likely to change their mind.
const cancellationDeadline = (start) =>
  start ? new Date(start.getTime() - CANCELLATION_CUTOFF_MS) : null;

// Where a cancelled booking's money actually is. Without this a traveller sees
// "Cancelled" and nothing else, and writes to support to ask where their money
// went. `refund` covers the card half only — wallet credit is returned the
// moment the booking is cancelled, so there is nothing to wait for there.
const REFUND_STATUS_NOTICES = {
  pending: (amount) => `${amount} refund approved and being prepared.`,
  processing: (amount) => `${amount} is on its way back to the account you paid with.`,
  success: (amount) => `${amount} has been refunded.`,
  failed: (amount) =>
    `${amount} refund needs attention — our team is on it. Contact support if you have been waiting.`,
};

const buildRefundStatusNotice = (refund) => {
  if (!refund?.status) return null;
  const describe = REFUND_STATUS_NOTICES[refund.status];
  if (!describe) return null;
  return describe(formatCurrency(refund.amount || 0));
};

// Either the deadline a traveller still has to beat, or why the button they
// are looking for is gone.
const buildRefundNotice = ({ status, deadline }) => {
  if (!CANCELLABLE_STATUSES.has(status)) return null;
  if (!deadline) return null;

  return deadline.getTime() > Date.now()
    ? `Cancel before ${formatDayTime(deadline)} to get everything back except Pulo's 20% commission.`
    : `Cancellations closed ${CANCELLATION_CUTOFF_HOURS} hours before your trip starts.`;
};

const formatDateRange = (start, end) => {
  if (!start) return "—";
  if (!end || formatDay(start) === formatDay(end)) return formatDay(start);
  return `${formatDay(start)} – ${formatDay(end)}`;
};

// The list endpoint populates the assistant; other responses (a confirmation,
// say) carry only the id. Handle both so a name never turns into an ObjectId.
const resolveAssistant = (assistant) => {
  if (!assistant || typeof assistant !== "object") {
    return { id: assistant ? String(assistant) : "", name: "Pulo Assistant", image: "" };
  }
  const picture = assistant.profilePicture;
  return {
    id: String(assistant._id || assistant.id || ""),
    name:
      [assistant.firstName, assistant.lastName].filter(Boolean).join(" ").trim() ||
      assistant.email ||
      "Pulo Assistant",
    image: (typeof picture === "string" ? picture : picture?.url) || "",
  };
};

export const mapBookingToTrip = (booking = {}) => {
  const status = normalizeStatus(booking.status);
  const assistant = resolveAssistant(booking.assistant);
  const start = toDate(booking.arrivalDate);
  const end = toDate(booking.departureDate);
  const createdAt = toDate(booking.createdAt);
  const days = booking.days || 1;

  const costBreakdown = [
    {
      label: `Assistant fee (${days} day${days > 1 ? "s" : ""})`,
      amount: formatCurrency(
        (booking.effectivePricePerDay || booking.pricePerDaySnapshot || 0) * days
      ),
    },
  ];
  if (booking.addOnsTotal) {
    costBreakdown.push({
      label: "Add-ons (vehicles / security)",
      amount: formatCurrency(booking.addOnsTotal),
    });
  }
  if (booking.creditApplied) {
    costBreakdown.push({
      label: "Wallet credit applied",
      amount: `-${formatCurrency(booking.creditApplied)}`,
    });
  }

  const deadline = cancellationDeadline(start);
  const flags = buildConfirmationFlags({ ...booking, status });
  const actionLabel = flags.canConfirmStart
    ? "Confirm trip started"
    : flags.canConfirmEnd
    ? "Confirm trip ended"
    : null;

  return {
    id: String(booking._id || booking.id || ""),
    status,
    statusLabel: BOOKING_STATUS_LABELS[status] || booking.status || "Unknown",
    tab: getTripTab(status),
    assistantId: assistant.id,
    assistantName: assistant.name,
    assistantImage: assistant.image,
    startsAt: start ? start.getTime() : null,
    dateRange: formatDateRange(start, end),
    bookedOn: createdAt ? formatDay(createdAt) : "—",
    reference: booking.paymentReference || String(booking._id || ""),
    itinerary: booking.itinerary || "",
    costBreakdown,
    total: formatCurrency(booking.amountPaid || booking.amountExpected || 0),
    // The server refuses a cancellation inside the deadline, so the button
    // goes with it rather than leading the traveller into an error.
    cancellable:
      CANCELLABLE_STATUSES.has(status) &&
      Boolean(deadline) &&
      deadline.getTime() > Date.now(),
    refundStatus: booking.refund?.status || null,
    refundStatusNotice: buildRefundStatusNotice(booking.refund),
    refundWindowEndsAt: deadline ? deadline.getTime() : null,
    refundNotice: buildRefundNotice({ status, deadline }),
    ...flags,
    actionLabel,
    needsAction: Boolean(actionLabel),
  };
};

/**
 * The trips waiting on the traveller's own confirmation, soonest first.
 *
 * This is what the action bar shows on every page: the confirmation used to
 * live only behind My Trips → open the trip, so a traveller standing next to
 * their assistant had to go digging through history to start the trip — and
 * the assistant's fee stayed locked until they found it.
 */
export const getTripsNeedingAction = (bookings = []) =>
  bookings
    .map(mapBookingToTrip)
    .filter((trip) => trip.needsAction)
    // An end confirmation is the more urgent of the two — it is the one
    // holding the second half of the assistant's fee.
    .sort((a, b) => {
      if (a.canConfirmEnd !== b.canConfirmEnd) return a.canConfirmEnd ? -1 : 1;
      return (a.startsAt ?? Infinity) - (b.startsAt ?? Infinity);
    });

const byStart = (fallback, direction) => (a, b) => {
  const x = a.startsAt ?? fallback;
  const y = b.startsAt ?? fallback;
  return x === y ? 0 : (x - y) * direction;
};

/** Upcoming and in-progress trips soonest first; past trips most recent first. */
export const groupTripsByTab = (bookings = []) => {
  const groups = { upcoming: [], in_progress: [], past: [] };
  bookings.map(mapBookingToTrip).forEach((trip) => groups[trip.tab].push(trip));
  groups.upcoming.sort(byStart(Infinity, 1));
  groups.in_progress.sort(byStart(Infinity, 1));
  groups.past.sort(byStart(-Infinity, -1));
  return groups;
};
